/**
 * Application error catalog — single source of truth for the frontend.
 *
 * This file is consumed by:
 *   - `frontend/src/lib/errorCodes.ts` (mapBetterAuthSlug lookup)
 *   - `frontend/src/pages/Admin/components/ErrorCodesReferenceTab.tsx` (UI table)
 *
 * The canonical human-readable spec lives at `docs/error-codes.md`. When
 * adding a code, update BOTH this file and the markdown doc.
 */

export interface ErrorCatalogRow {
	/** 5-character application code, e.g. "10001". */
	code: string;
	/** SCREAMING_SNAKE constant name. */
	name: string;
	/** HTTP status the backend pairs with this code. Omitted for FE-only codes. */
	http?: number;
	/** Short Vietnamese description shown in the admin reference table. */
	description: string;
	/**
	 * Better Auth error slugs (case-insensitive) that should be mapped to this
	 * code by `mapBetterAuthSlug`. Multiple slugs may share one code.
	 */
	betterAuthSlugs?: string[];
}

export interface ErrorCatalogGroup {
	/** Display label for the range, e.g. "10xxx". */
	range: string;
	/** Group display name, e.g. "Auth". */
	name: string;
	/** Group-level description shown above the table. */
	description: string;
	/** Codes in this group are produced only on the client, never by backend. */
	frontendOnly?: boolean;
	rows: ErrorCatalogRow[];
}

export const ERROR_CATALOG: ErrorCatalogGroup[] = [
	{
		range: '10xxx',
		name: 'Auth',
		description: 'Đăng nhập / phiên / xác thực (Better Auth + middleware).',
		rows: [
			{
				code: '10001', name: 'AUTH_INVALID_CREDENTIALS', http: 401,
				description: 'Sai email/username/password.',
				betterAuthSlugs: ['INVALID_EMAIL_OR_PASSWORD', 'INVALID_PASSWORD', 'INVALID_USERNAME_OR_PASSWORD'],
			},
			{
				code: '10002', name: 'AUTH_USER_NOT_FOUND', http: 404,
				description: 'Tài khoản không tồn tại.',
				betterAuthSlugs: ['USER_NOT_FOUND'],
			},
			{
				code: '10003', name: 'AUTH_EMAIL_NOT_VERIFIED', http: 403,
				description: 'Email chưa được xác thực.',
				betterAuthSlugs: ['EMAIL_NOT_VERIFIED'],
			},
			{
				code: '10004', name: 'AUTH_SESSION_EXPIRED', http: 401,
				description: 'Session hết hạn / không hợp lệ.',
				betterAuthSlugs: ['SESSION_EXPIRED'],
			},
			{ code: '10005', name: 'AUTH_SESSION_VALIDATION_FAILED', http: 401, description: 'Better Auth getSession ném exception.' },
			{ code: '10006', name: 'AUTH_NOT_AUTHENTICATED', http: 401, description: 'Truy cập endpoint cần đăng nhập mà chưa đăng nhập.' },
			{ code: '10007', name: 'AUTH_OAUTH_FAILED', http: 400, description: 'Đăng nhập Google / One Tap thất bại.' },
			{
				code: '10008', name: 'AUTH_ACCOUNT_LOCKED', http: 403,
				description: 'Tài khoản bị admin khoá / banned.',
				betterAuthSlugs: ['USER_BANNED', 'BANNED_USER'],
			},
			{
				code: '10009', name: 'AUTH_TOO_MANY_ATTEMPTS', http: 429,
				description: 'Rate-limit từ Better Auth hoặc middleware.',
				betterAuthSlugs: ['TOO_MANY_REQUESTS', 'RATE_LIMIT_EXCEEDED'],
			},
			{
				code: '10010', name: 'AUTH_SIGNUP_DISABLED', http: 403,
				description: 'Đăng ký tài khoản bị tắt trong production.',
				betterAuthSlugs: ['SIGNUP_DISABLED', 'EMAIL_AND_PASSWORD_SIGN_UP_DISABLED'],
			},
			{ code: '10011', name: 'AUTH_MUST_LOGOUT_RELOGIN', http: 403, description: 'Người dùng cần đăng xuất/đăng nhập lại để cập nhật quyền.' },
			{ code: '10012', name: 'AUTH_DEMOTE_SELF_FORBIDDEN', http: 400, description: 'Không thể tự hạ cấp vai trò.' },
			{ code: '10013', name: 'AUTH_DEMOTE_ADMIN_FORBIDDEN', http: 400, description: 'Không thể hạ cấp admin khác.' },
			{ code: '10014', name: 'AUTH_DELETE_SELF_FORBIDDEN', http: 400, description: 'Không thể xoá chính mình.' },
			{ code: '10015', name: 'AUTH_DELETE_ADMIN_FORBIDDEN', http: 400, description: 'Không thể xoá admin khác.' },
			{
				code: '10016', name: 'AUTH_USERNAME_EMAIL_TAKEN', http: 409,
				description: 'Username/email đã tồn tại.',
				betterAuthSlugs: ['USERNAME_ALREADY_EXISTS'],
			},
			{
				code: '10017', name: 'AUTH_EMAIL_TAKEN', http: 409,
				description: 'Email đã tồn tại.',
				betterAuthSlugs: ['EMAIL_ALREADY_EXISTS', 'USER_ALREADY_EXISTS'],
			},
			{ code: '10018', name: 'AUTH_NO_ADMIN_ACCOUNT', http: 500, description: 'Không tìm thấy admin trong hệ thống.' },
			{ code: '10019', name: 'AUTH_USER_CONTEXT_MISSING', http: 401, description: 'req.user thiếu trong controller.' },
			{
				code: '10020', name: 'AUTH_INVALID_ORIGIN', http: 403,
				description: 'Origin của request không nằm trong trustedOrigins. Better Auth chặn để chống CSRF.',
				betterAuthSlugs: ['INVALID_ORIGIN'],
			},
		],
	},
	{
		range: '20xxx',
		name: 'Validation',
		description: 'Dữ liệu vào không hợp lệ (Zod / validation manual).',
		rows: [
			{ code: '20001', name: 'VALIDATION_FAILED', http: 400, description: 'Zod schema fail. Kèm errors[] chi tiết.' },
			{ code: '20002', name: 'VALIDATION_MISSING_FIELD', http: 400, description: 'Thiếu field bắt buộc.' },
			{ code: '20003', name: 'VALIDATION_INVALID_TYPE', http: 400, description: 'Sai kiểu dữ liệu.' },
			{ code: '20004', name: 'VALIDATION_INVALID_LOG_LEVEL', http: 400, description: 'Log level không hợp lệ.' },
			{ code: '20005', name: 'VALIDATION_INVALID_TABLE', http: 400, description: 'Tên bảng không nằm trong danh sách cho phép.' },
			{ code: '20006', name: 'VALIDATION_INVALID_FORMAT', http: 400, description: 'Định dạng export không hợp lệ.' },
			{ code: '20007', name: 'VALIDATION_CONFIRMATION_REQUIRED', http: 400, description: 'Yêu cầu xác nhận DELETE ALL.' },
			{ code: '20008', name: 'VALIDATION_FILE_REQUIRED', http: 400, description: 'Thiếu file upload.' },
			{ code: '20009', name: 'VALIDATION_FILE_TYPE', http: 400, description: 'File sai mime/size.' },
			{ code: '20010', name: 'VALIDATION_BACKUP_FORMAT', http: 400, description: 'File backup không đúng pg_dump -Fc, hoặc khác phiên bản schema với hệ thống.' },
			{ code: '20011', name: 'VALIDATION_MISSING_SESSION_ID', http: 400, description: 'Thiếu sessionId nhập liệu.' },
			{ code: '20012', name: 'VALIDATION_IMPORT_TYPE_MISMATCH', http: 400, description: 'Loại import không khớp session.' },
			{ code: '20013', name: 'VALIDATION_MISSING_STUDENT_ID', http: 400, description: 'Thiếu mã sinh viên khi import.' },
		],
	},
	{
		range: '30xxx',
		name: 'Network',
		description: 'Sinh tại client khi không nhận được response. Backend không bao giờ trả về nhóm này.',
		frontendOnly: true,
		rows: [
			{ code: '30001', name: 'NETWORK_TIMEOUT', description: 'Request quá timeout (axios 15000ms, export 60000ms).' },
			{ code: '30002', name: 'NETWORK_OFFLINE', description: 'navigator.onLine === false.' },
			{ code: '30003', name: 'NETWORK_UNREACHABLE', description: 'Fetch/axios fail mà không có response (DNS, CORS preflight reject, …).' },
			{ code: '30004', name: 'NETWORK_ABORTED', description: 'Request bị huỷ bởi AbortController / page navigate.' },
		],
	},
	{
		range: '40xxx',
		name: 'Server',
		description: 'Lỗi nội bộ backend, không phải lỗi người dùng.',
		rows: [
			{ code: '40001', name: 'SERVER_INTERNAL', http: 500, description: 'Fallback khi không phân loại được.' },
			{ code: '40002', name: 'SERVER_UNAVAILABLE', http: 503, description: 'Dịch vụ phụ thuộc không sẵn sàng (Redis down…).' },
			{ code: '40003', name: 'SERVER_LOG_READ_FAILED', http: 500, description: 'Không đọc được backend logs.' },
			{ code: '40004', name: 'SERVER_STATS_FAILED', http: 500, description: 'Lỗi tính toán thống kê.' },
			{ code: '40005', name: 'SERVER_BACKUP_FAILED', http: 500, description: 'Lỗi pg_dump.' },
			{ code: '40006', name: 'SERVER_RESTORE_FAILED', http: 500, description: 'Lỗi pg_restore.' },
			{ code: '40007', name: 'SERVER_SCOPE_ENFORCEMENT', http: 500, description: 'Lỗi khi áp dụng faculty-major scope.' },
			{ code: '40008', name: 'SERVER_UNHANDLED_PROMISE', http: 500, description: 'Promise rejection không catch (process handler).' },
			{ code: '40009', name: 'SERVER_MAIL_NOT_CONFIGURED', http: 503, description: 'Chưa cấu hình SMTP nên không gửi được email.' },
			{ code: '40010', name: 'SERVER_MAIL_SEND_FAILED', http: 502, description: 'Máy chủ SMTP từ chối hoặc không kết nối được khi gửi email.' },
		],
	},
	{
		range: '50xxx',
		name: 'Permission',
		description: 'Phân quyền.',
		rows: [
			{ code: '50001', name: 'PERMISSION_DENIED', http: 403, description: 'Thiếu quyền chung.' },
			{ code: '50002', name: 'PERMISSION_ADMIN_REQUIRED', http: 403, description: 'Cần role admin.' },
			{ code: '50003', name: 'PERMISSION_WRITE_REQUIRED', http: 403, description: 'Cần writable manager hoặc admin.' },
			{ code: '50004', name: 'PERMISSION_FACULTY_SCOPE', http: 403, description: 'User chỉ được xem khoa của mình; query bị từ chối.' },
		],
	},
	{
		range: '60xxx',
		name: 'Resource',
		description: 'Tài nguyên không tồn tại / trùng / xung đột.',
		rows: [
			{ code: '60001', name: 'RESOURCE_NOT_FOUND', http: 404, description: 'Generic 404.' },
			{ code: '60002', name: 'RESOURCE_PROVINCE_NOT_FOUND', http: 404, description: 'Tỉnh thành không tìm thấy.' },
			{ code: '60003', name: 'RESOURCE_THPT_NOT_FOUND', http: 404, description: 'Trường THPT không tìm thấy.' },
			{ code: '60004', name: 'RESOURCE_IMPORT_SESSION_GONE', http: 404, description: 'Phiên nhập liệu hết hạn / không tồn tại.' },
			{ code: '60005', name: 'RESOURCE_CONFLICT', http: 409, description: 'Generic 409.' },
			{ code: '60008', name: 'RESOURCE_MIGRATION_DUPLICATE', http: 409, description: 'Mã trường cũ đã có trong migration records.' },
		],
	},
	{
		range: '70xxx',
		name: 'Business',
		description: 'Lỗi nghiệp vụ chuyên biệt (import, export, backup).',
		rows: [
			{ code: '70001', name: 'IMPORT_VALIDATION_ERRORS', http: 400, description: 'File import có lỗi cần sửa. Kèm data.errors[].' },
			{ code: '70002', name: 'IMPORT_CANCELLED', http: 499, description: 'Người dùng huỷ import.' },
			{ code: '70003', name: 'IMPORT_DIAGNOSE_ROLLBACK', http: 500, description: 'Phát hiện vấn đề → rollback.' },
			{ code: '70004', name: 'IMPORT_FAILED', http: 500, description: 'Lỗi tổng quát khi xử lý import.' },
			{ code: '70005', name: 'EXPORT_TABLE_UNSUPPORTED', http: 400, description: 'Bảng không được hỗ trợ export.' },
			{ code: '70006', name: 'DATA_DELETION_FAILED', http: 500, description: 'Xoá dữ liệu thất bại.' },
			{ code: '70007', name: 'TEMPLATE_TYPE_INVALID', http: 400, description: 'Loại Excel template không hợp lệ.' },
			{ code: '70008', name: 'EXPORT_IN_PROGRESS', http: 429, description: 'Đang có một tiến trình export chạy, thử lại sau.' },
		],
	},
	{
		range: '80xxx',
		name: 'Database',
		description: 'Prisma errors (err.code bắt đầu bằng P).',
		rows: [
			{ code: '80001', name: 'DB_UNIQUE_CONSTRAINT', http: 409, description: 'P2002 — Bản ghi đã tồn tại.' },
			{ code: '80002', name: 'DB_FOREIGN_KEY_CONSTRAINT', http: 400, description: 'P2003 — Không thể xoá: còn bản ghi liên quan.' },
			{ code: '80003', name: 'DB_RECORD_NOT_FOUND', http: 404, description: 'P2025 — Không tìm thấy bản ghi.' },
			{ code: '80099', name: 'DB_GENERIC', http: 500, description: 'P* — Lỗi database khác.' },
		],
	},
	{
		range: '9xxxx',
		name: 'Unknown',
		description: 'Frontend fallback khi không trích xuất được mã lỗi.',
		frontendOnly: true,
		rows: [
			{ code: '90001', name: 'UNKNOWN_ERROR', description: 'Không trích xuất được code từ error.' },
			{ code: '90002', name: 'PARSE_ERROR', description: 'Response body không parse được thành JSON.' },
			{ code: '90003', name: 'EMPTY_RESPONSE', description: 'Response rỗng / null khi expect data.' },
			{ code: '90004', name: 'UNEXPECTED_SHAPE', description: 'Response thiếu trường bắt buộc.' },
			{ code: '90005', name: 'UNMAPPED_SLUG', description: 'Backend trả slug error nhưng frontend chưa có mapping.' },
			{ code: '90006', name: 'NULL_PAYLOAD', description: 'API trả null/undefined khi đáng lẽ phải có body lỗi.' },
		],
	},
];

// Build the slug → code lookup once at module load. Keys are uppercased so the
// caller can pass mixed-case slugs without normalising first.
const BETTER_AUTH_SLUG_TO_CODE: ReadonlyMap<string, string> = (() => {
	const map = new Map<string, string>();
	for (const group of ERROR_CATALOG) {
		for (const row of group.rows) {
			if (!row.betterAuthSlugs) continue;
			for (const slug of row.betterAuthSlugs) {
				const key = slug.toUpperCase();
				if (map.has(key) && map.get(key) !== row.code) {
					// Catch authoring mistakes early — same slug pointing at two codes
					// would silently mis-route errors in production.
					throw new Error(`Duplicate Better Auth slug "${slug}" mapped to both ${map.get(key)} and ${row.code}`);
				}
				map.set(key, row.code);
			}
		}
	}
	return map;
})();

/** Look up the 5-digit code for a Better Auth slug (case-insensitive). */
export function lookupBetterAuthSlug(slug: string | undefined | null): string | null {
	if (!slug) return null;
	return BETTER_AUTH_SLUG_TO_CODE.get(slug.toUpperCase()) ?? null;
}
